import React from 'react';
import { Link } from 'react-router-dom';
import { Star, DollarSign, Briefcase, Calendar, ArrowRight, CheckCircle, Clock } from 'lucide-react';
import { MOCK_PROJECTS, MOCK_APPOINTMENTS } from '../../mock';
import { useAppStore } from '../../store/useAppStore';
import Badge from '../../components/common/Badge';

const ContractorDashboard: React.FC = () => {
  const { user } = useAppStore();
  const myJobs = MOCK_PROJECTS.filter(p => p.contractorId === 'u-003');
  const upcomingAppts = MOCK_APPOINTMENTS.filter(a => a.contractorId === 'u-003');

  const stats = [
    { label: 'Active Jobs', value: myJobs.filter(j => j.status === 'in_progress').length.toString(), icon: <Briefcase size={20} color="#f97316" />, bg: '#fff7ed' },
    { label: 'Completed (MTD)', value: '14', icon: <CheckCircle size={20} color="#10b981" />, bg: '#dcfce7' },
    { label: 'Upcoming Appts', value: upcomingAppts.length.toString(), icon: <Calendar size={20} color="#3b82f6" />, bg: '#dbeafe' },
    { label: 'MTD Earnings', value: '$8,420', icon: <DollarSign size={20} color="#8b5cf6" />, bg: '#f5f3ff' },
  ];

  return (
    <div>
      {/* Welcome */}
      <div style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)', borderRadius: '1.25rem', padding: '2rem', marginBottom: '2rem', color: 'white' }}>
        <h2 style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: '0.375rem' }}>Good morning, {user?.name?.split(' ')[0]}! 🏗️</h2>
        <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.9rem' }}>You have {myJobs.filter(j => j.status === 'in_progress').length} active job(s) and {upcomingAppts.length} upcoming appointment(s) today.</p>
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
          <Link to="/contractor/jobs" className="btn-primary" style={{ fontSize: '0.875rem' }}>View My Jobs <ArrowRight size={14} /></Link>
          <Link to="/contractor/schedule" className="btn-secondary" style={{ fontSize: '0.875rem', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}>My Schedule</Link>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {stats.map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: '1rem', padding: '1.25rem', display: 'flex', gap: '0.875rem', alignItems: 'center', border: '1px solid var(--border-color)' }}>
            <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.625rem', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.icon}</div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a' }}>{s.value}</div>
              <div style={{ fontSize: '0.75rem', color: '#475569', fontWeight: 500 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* My Jobs */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>My Active Jobs</h3>
            <Link to="/contractor/jobs" style={{ fontSize: '0.8rem', color: '#f97316', fontWeight: 600 }}>View All</Link>
          </div>
          {myJobs.map(job => (
            <div key={job.id} style={{ padding: '0.875rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div>
                  <p style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', margin: 0 }}>{job.title}</p>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>{job.customerName}</p>
                </div>
                <Badge label={job.status.replace('_', ' ')} variant={job.status === 'completed' ? 'success' : job.status === 'in_progress' ? 'orange' : 'warning'} />
              </div>
              <div style={{ background: 'var(--border-color)', borderRadius: '9999px', height: '6px', overflow: 'hidden' }}>
                <div style={{ background: 'linear-gradient(90deg, #f97316, #ea580c)', height: '100%', width: `${job.progress}%`, borderRadius: '9999px' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                <span>{job.progress}% complete</span>
                <button style={{ background: 'none', border: 'none', color: '#f97316', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600 }}>Update Progress</button>
              </div>
            </div>
          ))}
        </div>

        {/* Schedule */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Upcoming Schedule</h3>
            <Link to="/contractor/schedule" style={{ fontSize: '0.8rem', color: '#f97316', fontWeight: 600 }}>Full Calendar</Link>
          </div>
          {upcomingAppts.map(appt => (
            <div key={appt.id} style={{ display: 'flex', gap: '0.875rem', marginBottom: '1rem', alignItems: 'center' }}>
              <div style={{ width: '3rem', height: '3rem', borderRadius: '0.625rem', background: 'rgb(249 115 22 / 0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '0.6rem', color: '#f97316', fontWeight: 700 }}>
                  {new Date(appt.date).toLocaleString('default', { month: 'short' }).toUpperCase()}
                </span>
                <span style={{ fontSize: '1rem', fontWeight: 800, color: '#f97316' }}>{new Date(appt.date).getDate()}</span>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', margin: 0 }}>{appt.customerName}</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.2rem 0 0', textTransform: 'capitalize' }}>
                  {appt.type} · {appt.timeSlot} ({appt.duration} min)
                </p>
              </div>
              <Badge label={appt.status} variant={appt.status === 'confirmed' ? 'success' : 'warning'} />
            </div>
          ))}

          {upcomingAppts.length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', textAlign: 'center', padding: '2rem 0' }}>No upcoming appointments.</p>
          )}
        </div>

        {/* Performance */}
        <div className="card">
          <h3 style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '1.25rem' }}>My Performance</h3>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
            {[1,2,3,4,5].map(i => <Star key={i} size={20} fill={i <= 4 ? '#f97316' : '#e2e8f0'} stroke="none" />)}
            <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '1.1rem' }}>4.9</span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', alignSelf: 'center' }}>/ 127 reviews</span>
          </div>
          {[
            { label: 'On-time completion', value: 98, color: '#10b981' },
            { label: 'Customer satisfaction', value: 96, color: '#3b82f6' },
            { label: 'Quality score', value: 99, color: '#f97316' },
          ].map(metric => (
            <div key={metric.label} style={{ marginBottom: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.3rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{metric.label}</span>
                <span style={{ fontWeight: 700, color: metric.color }}>{metric.value}%</span>
              </div>
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '9999px', height: '6px', overflow: 'hidden' }}>
                <div style={{ background: metric.color, height: '100%', width: `${metric.value}%`, borderRadius: '9999px' }} />
              </div>
            </div>
          ))}
        </div>

        {/* Earnings */}
        <div className="card">
          <h3 style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '1.25rem' }}>Earnings Summary</h3>
          {[
            { label: 'This Month', value: '$8,420', change: '+12%', positive: true },
            { label: 'Last Month', value: '$7,510', change: null, positive: null },
            { label: 'Year to Date', value: '$47,890', change: '+18%', positive: true },
            { label: 'Pending Payout', value: '$3,200', change: null, positive: null },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid var(--border-color)' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{item.label}</span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{item.value}</span>
                {item.change && (
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: item.positive ? '#10b981' : '#ef4444' }}>{item.change}</span>
                )}
              </div>
            </div>
          ))}
          <Link to="/contractor/earnings" className="btn-primary" style={{ marginTop: '1.25rem', width: '100%', textAlign: 'center' }}>
            View Full Earnings Report
          </Link>
        </div>
      </div>

      <style>{`@media(max-width:768px){div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}}`}</style>
    </div>
  );
};

export default ContractorDashboard;
